import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMergerProgress, MergerStep } from "shared/hooks/useMergerProgress";
import { Progress, Card, CardBody, Spinner } from "@heroui/react";
import {
  CheckCircle,
  XCircle,
  Clock,
  ArrowsMerge,
  Warning,
  Buildings,
} from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { Helmet, HelmetProvider } from "react-helmet-async";

const MergerProgressPage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { progress, error, isConnected } = useMergerProgress(jobId || null);

  useEffect(() => {
    if (!jobId) {
      navigate("/vennootschappen");
    }
  }, [jobId, navigate]);

  const getStepIcon = (step: MergerStep) => {
    switch (step) {
      case MergerStep.VALIDATING:
        return <Clock size={24} className="animate-pulse text-blue-500" />;
      case MergerStep.UPDATING_DATABASE:
      case MergerStep.UPDATING_BUILDINGS:
      case MergerStep.UPDATING_PRODUCTS:
      case MergerStep.UPDATING_ODOO:
        return <Spinner size="sm" />;
      case MergerStep.COMPLETED:
        return (
          <CheckCircle size={24} className="text-moss-primary" weight="fill" />
        );
      case MergerStep.FAILED:
        return (
          <XCircle size={24} className="text-brick-primary" weight="fill" />
        );
      default:
        return <Clock size={24} />;
    }
  };

  const getContractIcon = (status: string) => {
    switch (status) {
      case "pending":
        return <Clock size={16} className="text-gray-400" />;
      case "processing":
        return <Spinner size="sm" />;
      case "success":
        return (
          <CheckCircle size={16} className="text-moss-primary" weight="fill" />
        );
      case "failed":
        return (
          <XCircle size={16} className="text-brick-primary" weight="fill" />
        );
      default:
        return <Clock size={16} className="text-gray-400" />;
    }
  };

  const progressPercent = progress?.totalContracts
    ? (progress.processedContracts / progress.totalContracts) * 100
    : 0;

  if (!jobId) {
    return null;
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardBody className="text-center">
            <Warning size={48} className="mx-auto mb-4 text-yellow-500" />
            <h2 className="mb-2 text-xl font-semibold">Verbinding verloren</h2>
            <p className="mb-4 text-gray-600">{error}</p>
            <ButtonNew
              variant="primaryBrick"
              onClick={() => navigate("/vennootschappen")}
              label="Terug naar overzicht"
            />
          </CardBody>
        </Card>
      </div>
    );
  }

  if (!progress) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" className="mb-4" />
          <p className="text-gray-600">Voortgang laden...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Fusie Voortgang &mdash; Yally</title>
        </Helmet>
      </HelmetProvider>

      <div className="mx-auto max-w-7xl space-y-6 p-6">
        {/* Header */}
        <Card>
          <CardBody>
            <div className="flex items-center gap-4">
              <ArrowsMerge size={32} className="text-brick-primary" />
              <div className="flex-1">
                <h1 className="text-2xl font-semibold">Bedrijven Fuseren</h1>
                <p className="mt-1 text-sm text-gray-600">
                  Job ID: {jobId}
                  {isConnected && (
                    <span className="ml-2 inline-flex items-center gap-1">
                      <span className="bg-moss-primary h-2 w-2 animate-pulse rounded-full"></span>
                      <span className="text-moss-primary text-xs">Live</span>
                    </span>
                  )}
                </p>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Current Step */}
        <Card>
          <CardBody>
            <div className="flex items-center gap-4">
              {getStepIcon(progress.step)}
              <div className="flex-1">
                <h3 className="text-lg font-semibold">
                  {progress.currentStepDescription}
                </h3>
                {progress.totalContracts > 0 && (
                  <p className="mt-1 text-sm text-gray-600">
                    Contract {progress.processedContracts} van{" "}
                    {progress.totalContracts} ({progress.successfulContracts}{" "}
                    succesvol, {progress.failedContracts} mislukt)
                  </p>
                )}
              </div>
            </div>

            {progress.totalContracts > 0 && (
              <div className="mt-4">
                <Progress
                  value={progressPercent}
                  color={
                    progress.step === MergerStep.FAILED ? "danger" : "primary"
                  }
                  className="w-full"
                  size="lg"
                  showValueLabel={true}
                />
              </div>
            )}
          </CardBody>
        </Card>

        {/* Contract List */}
        {progress.contracts.length > 0 && (
          <Card>
            <CardBody>
              <div className="mb-4 flex items-center gap-2">
                <Buildings size={24} />
                <h3 className="text-lg font-semibold">
                  Contracten ({progress.contracts.length})
                </h3>
              </div>

              <div className="max-h-96 space-y-2 overflow-y-auto">
                {progress.contracts.map((contract) => (
                  <div
                    key={contract.contractId}
                    className="flex items-start gap-3 rounded-lg border border-gray-200 p-3 transition-colors hover:bg-gray-50"
                  >
                    <div className="mt-0.5">
                      {getContractIcon(contract.status)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">
                          Contract {contract.contractId}
                        </span>
                        <span className="text-gray-400">•</span>
                        <span className="text-sm text-gray-600">
                          {contract.customerName}
                        </span>
                        <span className="text-gray-400">•</span>
                        <span className="text-sm text-gray-500">
                          Gebouw: {contract.buildingId}
                        </span>
                      </div>
                      {contract.error && (
                        <p className="text-brick-primary mt-1 text-sm">
                          {contract.error}
                        </p>
                      )}
                      {contract.priceChanges &&
                        contract.priceChanges.length > 0 && (
                          <div className="mt-2 space-y-1">
                            <p className="text-xs font-medium text-gray-700">
                              Prijs aanpassingen:
                            </p>
                            {contract.priceChanges.map((change, idx) => {
                              const priceUnitMatch =
                                change.oldPriceUnit === change.newPriceUnit;

                              return (
                                <div
                                  key={idx}
                                  className="pl-4 text-xs text-gray-600"
                                >
                                  <span className="font-medium">
                                    {change.productName}
                                  </span>
                                  <div className="mt-0.5 flex items-center gap-2">
                                    <span className="text-gray-500">
                                      Verwijderd line {change.oldOrderLineId}:
                                    </span>
                                    <span>
                                      price_unit: €
                                      {change.oldPriceUnit.toFixed(2)}
                                    </span>
                                    <span className="text-gray-500">|</span>
                                    <span>
                                      base_price: €
                                      {change.oldBasePrice.toFixed(2)}
                                    </span>
                                  </div>
                                  <div className="mt-0.5 flex items-center gap-2">
                                    <span className="text-moss-primary">
                                      Nieuw line {change.newOrderLineId}:
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <span className="font-medium">
                                        price_unit: €
                                        {change.newPriceUnit.toFixed(2)}
                                      </span>
                                      {priceUnitMatch ? (
                                        <span className="text-moss-primary">
                                          ✓
                                        </span>
                                      ) : (
                                        <span className="text-brick-primary">
                                          ✗
                                        </span>
                                      )}
                                    </span>
                                    <span className="text-gray-500">|</span>
                                    <span className="flex items-center gap-1">
                                      <span className="font-medium">
                                        base_price: €
                                        {change.oldBasePrice.toFixed(2)}
                                      </span>
                                      <span className="text-moss-primary">
                                        ✓
                                      </span>
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        )}

        {/* Errors */}
        {progress.errors.length > 0 && (
          <Card className="border-brick-primary">
            <CardBody>
              <div className="mb-4 flex items-center gap-2">
                <Warning size={24} className="text-brick-primary" />
                <h3 className="text-brick-primary text-lg font-semibold">
                  Fouten ({progress.errors.length})
                </h3>
              </div>
              <div className="max-h-60 space-y-2 overflow-y-auto rounded border border-gray-200 p-3">
                {progress.errors.slice(0, 20).map((error, idx) => (
                  <div key={idx} className="text-sm break-words text-gray-700">
                    {idx + 1}. {error}
                  </div>
                ))}
                {progress.errors.length > 20 && (
                  <p className="mt-2 text-sm text-gray-500 italic">
                    ... en {progress.errors.length - 20} meer fouten
                  </p>
                )}
              </div>
            </CardBody>
          </Card>
        )}

        {/* Summary (when completed) */}
        {progress.completed && progress.summary && (
          <Card
            className={
              progress.success ? "border-moss-primary" : "border-yellow-500"
            }
          >
            <CardBody>
              <div className="flex items-start gap-3">
                {progress.success ? (
                  <CheckCircle
                    size={32}
                    className="text-moss-primary flex-shrink-0"
                    weight="fill"
                  />
                ) : (
                  <Warning
                    size={32}
                    className="flex-shrink-0 text-yellow-500"
                  />
                )}
                <div className="flex-1">
                  <h3 className="mb-3 text-lg font-semibold">
                    {progress.success
                      ? "Fusie succesvol voltooid!"
                      : "Fusie voltooid met waarschuwingen"}
                  </h3>
                  <ul className="list-inside list-disc space-y-1 text-sm">
                    <li>
                      Gebouwen verplaatst: {progress.summary.buildingsMoved}
                    </li>
                    <li>
                      Contracten bijgewerkt: {progress.summary.contractsUpdated}
                    </li>
                    <li>
                      Producten bijgewerkt: {progress.summary.productsUpdated}
                    </li>
                    <li>
                      Odoo contracten bijgewerkt:{" "}
                      {progress.summary.odooContractsUpdated}
                    </li>
                    {progress.summary.odooContractLinesFailed > 0 && (
                      <li className="text-brick-primary">
                        Odoo order lines mislukt:{" "}
                        {progress.summary.odooContractLinesFailed}
                      </li>
                    )}
                  </ul>

                  <div className="mt-4">
                    <ButtonNew
                      variant="primaryMoss"
                      onClick={() => navigate("/vennootschappen")}
                      label="Terug naar vennootschappen"
                    />
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        )}
      </div>
    </>
  );
};

export default MergerProgressPage;
